import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ResourceDetailDrawer } from "@/components/dashboard/ResourceDetailDrawer";
import {
  type ResourceFilterOptions,
  ResourcesFilterBar,
} from "@/components/dashboard/resources-filters/ResourcesFilterBar";
import { DisciplineChip } from "@/components/shared/discipline-chip";
import { ErrorState } from "@/components/shared/error-state";
import { useResourceFilters } from "@/hooks/use-resource-filters";
import { COMPETENCY_ORDER, COMPETENCY_SCALE_MAX, competencyShort } from "@/lib/competency-colors";
import {
  CONSTRAINT_GROUP_ORDER,
  constraintCellBackground,
  constraintGroupHue,
} from "@/lib/constraint-colors";
import { jsonFetch } from "@/lib/json-fetch";
import { matchesResourceFilters } from "@/lib/resource-filters";

interface ResourceCard {
  id: string;
  name: string;
  email: string;
  initials: string;
  capacity: number;
  points: number;
  active: boolean;
  competencies: Array<{ name: string; proficiency: number | null }>;
  constraints: Array<{ id: string; name: string; group: string; rating: number | null }>;
  disciplines: Array<{ id: string; code: string; name: string }>;
  solutions: Array<{ id: string; name: string }>;
  accounts: Array<{ id: string; name: string }>;
}

// Compact capacity pill: load over capacity, tinted by utilisation. Structural
// read of bench load — over-allocation goes red, near-full amber, otherwise navy.
function CapacityPill({ used, capacity }: { used: number; capacity: number }) {
  const pct = capacity === 0 ? 0 : Math.min(100, (used / capacity) * 100);
  const over = used > capacity;
  const tone = over ? "var(--fit-at-risk)" : pct > 85 ? "var(--fit-moderate)" : "var(--brand-600)";
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="font-display text-[13px] font-bold leading-none tabular-nums">
        {used}
        <span className="text-[10px] font-semibold text-muted-foreground">/{capacity}</span>
      </span>
      <span className="h-1 w-10 overflow-hidden rounded-full bg-muted">
        <span
          className="block h-full rounded-full"
          style={{ width: `${pct}%`, background: tone }}
        />
      </span>
    </div>
  );
}

// Competency dot meters (design Phase 2, Option B): one 5-dot meter per competency,
// in the fixed C / PdM / PM order, filled left→right to the grade. Round teal dots
// are a deliberate sibling-but-distinct counterpart to the square blue/purple
// constraint density grid. Unrated (null) renders an all-empty meter so the gap
// reads as missing data, not a low grade.
function CompetencyMeter({ competencies }: { competencies: ResourceCard["competencies"] }) {
  const profByName = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const c of competencies) map.set(c.name, c.proficiency);
    return map;
  }, [competencies]);

  return (
    <div className="flex flex-col gap-1.5">
      {COMPETENCY_ORDER.map((name) => {
        const grade = profByName.get(name) ?? null;
        const filled = grade ?? 0;
        const readout = grade == null ? "unrated" : `${grade}/${COMPETENCY_SCALE_MAX}`;
        return (
          <div key={name} className="flex items-center gap-[9px]" title={`${name}: ${readout}`}>
            <span
              className="w-[26px] text-[9.5px] font-bold leading-none text-muted-foreground"
              aria-hidden
            >
              {competencyShort(name)}
            </span>
            <span className="flex gap-1" aria-hidden>
              {Array.from({ length: COMPETENCY_SCALE_MAX }, (_, i) => i + 1).map((step) => (
                <span
                  key={`${name}-${step}`}
                  className="h-[9px] w-[9px] rounded-full"
                  style={{
                    background: step <= filled ? "var(--comp-teal)" : "var(--comp-dot-empty)",
                  }}
                />
              ))}
            </span>
            <span className="sr-only">{`${name}: ${grade == null ? "unrated" : `${grade} of ${COMPETENCY_SCALE_MAX}`}`}</span>
          </div>
        );
      })}
    </div>
  );
}

// The per-row density block: one cluster per Constraint group, each a wrapped grid
// of intensity cells (hue = group, alpha = rating). This is what makes the bench
// read as a matrix when scanned vertically.
function ConstraintDensity({ constraints }: { constraints: ResourceCard["constraints"] }) {
  const byGroup = useMemo(() => {
    const map = new Map<string, ResourceCard["constraints"]>();
    for (const g of CONSTRAINT_GROUP_ORDER) map.set(g, []);
    for (const c of constraints) {
      const bucket = map.get(c.group);
      if (bucket) bucket.push(c);
    }
    // Strongest ratings first; unrated sink to the end of each cluster.
    for (const bucket of map.values()) {
      bucket.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
    }
    return map;
  }, [constraints]);

  if (constraints.length === 0) {
    return <span className="text-[10px] text-muted-foreground">No ratings yet</span>;
  }

  return (
    <div className="flex flex-col gap-[3px]">
      {CONSTRAINT_GROUP_ORDER.map((g) => {
        const items = byGroup.get(g) ?? [];
        if (items.length === 0) return null;
        const hue = constraintGroupHue(g);
        return (
          <div key={g} className="flex flex-wrap gap-[3px]">
            {items.map((c) => (
              <span
                key={c.id}
                className="h-3.5 w-3.5 rounded-[3px] transition-transform hover:scale-125"
                title={`${g} · ${c.name}: ${c.rating ?? "unrated"}`}
                style={
                  c.rating == null
                    ? { boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 25%, transparent)` }
                    : { background: constraintCellBackground(g, c.rating) }
                }
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function ResourcesPage() {
  const { state: filters, setState: setFilters, reset: resetFilters } = useResourceFilters();
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useQuery<{ resources: ResourceCard[] }>({
    queryKey: ["dashboard-resources"],
    queryFn: () => jsonFetch<{ resources: ResourceCard[] }>("/api/dashboard/resources"),
  });

  // Derive the available option sets from the loaded bench so every control reflects
  // the directory's actual coverage. Competencies are the fixed C / PdM / PM triad.
  const options = useMemo<ResourceFilterOptions>(() => {
    const disciplines = new Map<string, { code: string; name: string }>();
    const constraints = new Map<string, { id: string; name: string }>();
    const solutions = new Map<string, { id: string; name: string }>();
    const accounts = new Map<string, { id: string; name: string }>();
    let pointsCeiling = 0;
    for (const r of data?.resources ?? []) {
      for (const d of r.disciplines) if (!disciplines.has(d.code)) disciplines.set(d.code, d);
      for (const c of r.constraints)
        if (!constraints.has(c.id)) constraints.set(c.id, { id: c.id, name: c.name });
      for (const s of r.solutions) if (!solutions.has(s.id)) solutions.set(s.id, s);
      for (const a of r.accounts) if (!accounts.has(a.id)) accounts.set(a.id, a);
      pointsCeiling = Math.max(pointsCeiling, r.points, r.capacity);
    }
    const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
    return {
      disciplines: [...disciplines.values()].sort((a, b) => a.code.localeCompare(b.code)),
      competencies: [...COMPETENCY_ORDER],
      constraints: [...constraints.values()].sort(byName),
      solutions: [...solutions.values()].sort(byName),
      accounts: [...accounts.values()].sort(byName),
      pointsCeiling: pointsCeiling || 1,
    };
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return (data.resources ?? []).filter((r) => matchesResourceFilters(r, filters));
  }, [data, filters]);

  return (
    <div className="px-6 py-5">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-[20px] font-bold leading-tight">Resources</h1>
          <p className="text-[12px] text-muted-foreground">
            The bench — competencies, constraints, Disciplines, and current load.
          </p>
        </div>
        <ResourcesFilterBar
          state={filters}
          setState={setFilters}
          reset={resetFilters}
          options={options}
        />
      </div>

      {isLoading && (
        <div className="rounded-md border border-dashed border-border bg-card/50 px-6 py-12 text-center text-[12px] text-muted-foreground">
          Loading…
        </div>
      )}

      {error && !isLoading && (
        <ErrorState message="Failed to load resources." onRetry={() => refetch()} />
      )}

      {data && (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {/* Column header — mirrors the density-matrix mock. */}
          <div className="grid grid-cols-[minmax(200px,1.4fr)_72px_minmax(150px,0.9fr)_minmax(96px,0.6fr)_minmax(280px,2fr)_64px] items-center gap-3 border-b border-border bg-secondary px-4 py-2 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            <span>Resource</span>
            <span className="text-center">Capacity</span>
            <span title="Legacy — historical proficiency grade, read-only for audit. No longer feeds Fit Score; see Discipline Ratings on the Resource Detail screen.">
              Competencies (legacy)
            </span>
            <span>Discipline</span>
            <span>Constraint density</span>
            <span className="text-right">Rated</span>
          </div>

          <div className="divide-y divide-border">
            {filtered.map((r) => {
              const rated = r.constraints.filter((c) => c.rating != null);
              const ratedCount = rated.length;
              const score = rated.reduce((sum, c) => sum + (c.rating ?? 0), 0);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setOpenId(r.id)}
                  className="grid w-full grid-cols-[minmax(200px,1.4fr)_72px_minmax(150px,0.9fr)_minmax(96px,0.6fr)_minmax(280px,2fr)_64px] items-center gap-3 px-4 py-2.5 text-left transition hover:bg-brand-50/60"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground/85 text-[11px] font-semibold text-background">
                      {r.initials}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-display text-[13px] font-semibold leading-tight">
                        {r.name}
                      </span>
                      <span className="block truncate text-[10px] text-muted-foreground">
                        {r.email}
                      </span>
                    </span>
                  </div>

                  <div className="flex justify-center">
                    <CapacityPill used={r.points} capacity={r.capacity} />
                  </div>

                  <CompetencyMeter competencies={r.competencies} />

                  <div className="flex flex-wrap gap-1">
                    {r.disciplines.length === 0 ? (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    ) : (
                      r.disciplines.map((d) => (
                        <DisciplineChip key={d.id} code={d.code} title={d.name} />
                      ))
                    )}
                  </div>

                  <ConstraintDensity constraints={r.constraints} />

                  <div className="flex flex-col items-end leading-tight">
                    <span className="font-display text-[14px] font-bold tabular-nums">
                      {ratedCount}
                    </span>
                    <span
                      className="text-[9px] uppercase tracking-wide text-muted-foreground tabular-nums"
                      title="Sum of confirmed Constraint ratings"
                    >
                      Σ {score}
                    </span>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-6 py-12 text-center text-[12px] text-muted-foreground">
                No matches.
              </div>
            )}
          </div>
        </div>
      )}

      {openId && <ResourceDetailDrawer id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
